import type { RouteLocationNormalized } from 'vue-router'

// pilulka.cz/pilulka.sk referrers carry no marketing-attribution value - they're our own domains, not a traffic
// source - so a visit referred from either must never be logged, regardless of host (this covers cross-domain
// navigation between the two, which the same-host check below cannot).
const OWN_DOMAINS = ['pilulka.cz', 'pilulka.sk']

const isOwnDomainReferrer = (referrer: string): boolean => {
    let hostname: string
    try {
        hostname = new URL(referrer).hostname
    } catch {
        return false
    }

    return OWN_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
}

export default defineNuxtRouteMiddleware((to: RouteLocationNormalized) => {
    if (!process.server) {
        return
    }

    const source = (to.query?.utm_source as string) || null
    const medium = (to.query?.utm_medium as string) || null
    const campaign = (to.query?.utm_campaign as string) || null
    const term = (to.query?.utm_term as string) || null
    const content = (to.query?.utm_content as string) || null

    const headers = useRequestHeaders(['referer', 'cf-connecting-ip', 'x-forwarded-for', 'user-agent'])
    const referrer = headers.referer || null

    if (referrer !== null && isOwnDomainReferrer(referrer)) {
        return
    }

    const isExternalReferrer = referrer !== null && !referrer.includes(useRequestURL().host)

    if (!source && !isExternalReferrer) {
        return
    }

    // An anonymous visitor has no uid yet at this point (no basket exists) - sending this to the API right away
    // would either be dropped for lacking an identity, or force-create a basket/uid from a mere page view (which is
    // what flooded visit_tracking_log with a row per anonymous pageview). Stash it instead; the client-init plugin's
    // uid watcher sends it once a real uid exists (basket creation or login), so it still reconciles to the customer
    // exactly like an already-sent visit does.
    const pendingVisitTracking = usePendingVisitTrackingCookie()
    pendingVisitTracking.value = {
        source,
        medium,
        campaign,
        term,
        content,
        referrer,
        // behind Cloudflare, x-forwarded-for holds Cloudflare's own hop (and is trivially spoofable by the
        // client on the inbound request) - cf-connecting-ip is Cloudflare's own header for the true client
        // IP, so it takes precedence; x-forwarded-for's first hop is only a fallback for non-Cloudflare traffic
        ip: headers['cf-connecting-ip'] || headers['x-forwarded-for']?.split(',')[0]?.trim() || null,
        userAgent: headers['user-agent'] || null,
    }
})
